import * as types from './constants';
import { replaceTokensInUrl, isDev, getObjectAsQueryParams } from '@app/utils';
import endpoints from '../../constants/endpoints';
import { getHttpInstance } from '../../services/defaultHttpService';
import {
  MARKETING_PAGE_SEARCH_TYPE,
  STATUS,
  TIMEOUT_EXTENDED
} from '../../constants/commonConstants';
import { MARKETING_PRODUCT_PAGE_SIZE } from '@app/components/pages/marketing/constants';
import * as productTypes from '../products/constants';
import { marketingNormalizeCategoryDetails } from './utils';
import {
  createURLWithParams,
  filteredProductsCardsArr,
  getFacetsWithUOM
} from '@app/utils/products';
import { PCC_VIEW_ALL_LUCID_MARKETING_PAGE } from '@app/constants/featureFlags';
import { hasBothMetricAndUSSpecs } from '../products/utils';

const normalizeByPartNumberData = (data, initialFacet, initialRender) => {
  const newInitialFacet = initialFacet || [];
  const facetsArray =
    data.facets?.length > newInitialFacet?.length
      ? data?.facets
      : newInitialFacet;
  return marketingNormalizeCategoryDetails(data, facetsArray, initialRender);
};

export const getMarketingProductDetails =
  (partNumber, widgetId, initialRender = false) =>
  async (dispatch, getState) => {
    const timeout = isDev() ? TIMEOUT_EXTENDED : undefined;
    const http = getHttpInstance(timeout);
    const state = getState();
    const {
      pageNumber: storePageNumber,
      sortBy: orderBy,
      batchPartNumbers,
      initialFacet,
      filterByEquipment,
      activeFacets
    } = state.marketingLandingPageUI[widgetId]
      ? state.marketingLandingPageUI[widgetId]
      : {};

    const viewAllLucidEnabled =
      state.featureFlag?.[PCC_VIEW_ALL_LUCID_MARKETING_PAGE];
    const pageNumber = viewAllLucidEnabled
      ? storePageNumber - 1
      : storePageNumber;
    let productDetailsUrl;
    let equipmentQueryParams;

    if (filterByEquipment) {
      const { model, serialNumber } =
        state.myEquipment?.equipments?.selectedEquipment || {};
      equipmentQueryParams = getObjectAsQueryParams({
        equipmentModel: model,
        equipmentSerialNumber: serialNumber
      });
    }
    const newActiveFacet = activeFacets
      ?.map(({ facet }) => facet)
      ?.join('&facet=');
    const { storeId, langId, locale, catalogId } = state.common;
    if (viewAllLucidEnabled) {
      const urlQueryParams = {
        sortBy: orderBy,
        q: partNumber,
        langId,
        locale,
        storeId,
        offset: pageNumber,
        catalogId,
        filterByEquipment,
        searchType: MARKETING_PAGE_SEARCH_TYPE,
        facet: newActiveFacet,
        ...equipmentQueryParams
      };
      productDetailsUrl = createURLWithParams(urlQueryParams);
    } else {
      const partialUrl = replaceTokensInUrl(
        endpoints.MARKETING_PRODUCT_DETAILS,
        storeId,
        partNumber,
        langId,
        orderBy,
        MARKETING_PRODUCT_PAGE_SIZE,
        pageNumber
      );
      productDetailsUrl = `${partialUrl}&facet=${newActiveFacet}&${equipmentQueryParams}`;
    }

    dispatch({ type: productTypes.PRODUCTS_GET_DETAILS_BEGIN });
    dispatch({
      type: types.MARKETING_QUERY_PARAM,
      payload: {
        widgetId: widgetId,
        data: {
          getProductDetailsStatus: STATUS.PENDING,
          fireImpressionEvent: true
        }
      }
    });
    try {
      let { data } = await http.get(productDetailsUrl);

      data = viewAllLucidEnabled
        ? data.searchResponse
        : normalizeByPartNumberData(data, initialFacet, initialRender);

      let payload = {};
      let partNumbersArray = batchPartNumbers?.length ? batchPartNumbers : [];
      const datakey = viewAllLucidEnabled ? 'products' : 'catalogEntries';

      data?.[datakey]?.forEach(product => {
        payload = {
          ...payload,
          [product.partNumber]: {
            ...product
          }
        };
        partNumbersArray.push(product.partNumber);
      });

      dispatch({ type: productTypes.PRODUCTS_GET_DETAILS_SUCCESS, payload });
      const recordSetTotal = viewAllLucidEnabled
        ? data.productCount
        : data.recordSetTotal;

      const showSpecificationsToggle = viewAllLucidEnabled
        ? hasBothMetricAndUSSpecs(data.facets)
        : data.showSpecificationsToggle;

      dispatch({
        type: types.MARKETING_QUERY_PARAM,
        payload: {
          widgetId: widgetId,
          data: {
            getProductDetailsStatus: STATUS.RESOLVED,
            batchPartNumbers: partNumbersArray,
            facets: getFacetsWithUOM(data.facets),
            fbeEnabled: data.fbeEnabled,
            recordSetTotal,
            showSpecificationsToggle,
            ...(initialRender && { initialFacet: data.facets })
          }
        }
      });
    } catch (error) {
      dispatch({
        type: productTypes.PRODUCTS_GET_DETAILS_FAIL,
        payload: error
      });
      dispatch({
        type: types.MARKETING_QUERY_PARAM,
        payload: {
          widgetId: widgetId,
          data: {
            getProductDetailsStatus: STATUS.REJECTED
          }
        }
      });
    }
  };

export const setMarketingProducts =
  (partNumber, widgetId) => async dispatch => {
    dispatch({
      type: types.MARKETING_SET_PART_NUMBER,
      payload: { partNumber, widgetId }
    });
  };

export const marketingSetQueryParam =
  (params, fetchMarketingDetails = false) =>
  async (dispatch, getState) => {
    const state = getState();
    await dispatch({
      type: types.MARKETING_QUERY_PARAM,
      payload: { ...params }
    });
    const partNumbers =
      state.marketingLandingPageUI[params.widgetId]?.partNumbers;
    if (fetchMarketingDetails) {
      return dispatch(getMarketingProductDetails(partNumbers, params.widgetId));
    }
  };
export const setMarketingProductCardExpanded =
  (expandedId, widgetId) => (dispatch, getState) => {
    const state = getState();
    const { productCardExpandedArr = [] } = state.marketingLandingPageUI[
      widgetId
    ]
      ? state.marketingLandingPageUI[widgetId]
      : {};
    return dispatch({
      type: types.MARKETING_SET_PRODUCT_CARD_EXPANDED,
      payload: {
        productCardExpandedArr: filteredProductsCardsArr(
          productCardExpandedArr,
          expandedId
        ),
        widgetId
      }
    });
  };
